import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Customer } from '../types/finance';
import { getCustomers, createCustomer, updateCustomer, type CustomerCreateDto, type CustomerPatchDto } from '../lib/api/endpoints';
import { customerFromDto } from '../lib/api/mappers';

/**
 * FE-32 — los clientes del tenant. Se piden completos (con los inactivos) y se
 * reparten en memoria, igual que el catálogo de categorías: el formulario
 * ofrece los activos, Administración necesita ver los inactivos para
 * reactivarlos, y el filtro del listado ofrece todos.
 */
export function useCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await getCustomers();
        if (cancelled) return;
        setCustomers(res.items.map(customerFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setCustomers([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando los clientes'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  const upsert = useCallback((c: Customer) => {
    setCustomers(prev => {
      const idx = prev.findIndex(x => x.id === c.id);
      if (idx === -1) return [...prev, c].sort((a, b) => a.name.localeCompare(b.name));
      const next = [...prev];
      next[idx] = c;
      return next;
    });
  }, []);

  const acciones = useMemo(() => ({
    crear: async (data: CustomerCreateDto): Promise<Customer> => {
      const c = customerFromDto(await createCustomer(data));
      upsert(c);
      return c;
    },
    actualizar: async (customerId: string, data: CustomerPatchDto): Promise<Customer> => {
      const c = customerFromDto(await updateCustomer(customerId, data));
      upsert(c);
      return c;
    },
  }), [upsert]);

  const activos = useMemo(() => customers.filter(c => c.active).sort((a, b) => a.name.localeCompare(b.name)), [customers]);

  return { customers, activos, isLoading, error, reload, ...acciones };
}
